// The portal's own sign-in (native sign-in environments): email first, then the password (or an email code for an
// account that signs in with codes). Done, the page reloads signed in, where it was. Too many wrong passwords
// lock the account for a while; that gets its own screen with the way out.
import { useState } from "react";
import type { SignInCodeRequiredState, SignInPasswordRequiredState } from "@azure/msal-browser/custom-auth";
import { useAuth } from "../auth/AuthProvider";
import { isExpired, isLocked, signInScopes, wrong } from "../auth/native";
import { AuthActions, AuthBadge, AuthFrame, AuthHeading, authNote, authText, CodeInput, EmailChip, PasswordInput, TextButton, useCountdown } from "../shared/AuthFrame";
import { Button, Icon, Input } from "../ui";

type Step =
  | { kind: "email" }
  | { kind: "password"; state: SignInPasswordRequiredState }
  | { kind: "code"; state: SignInCodeRequiredState }
  | { kind: "locked" };

/** The sign-in result's next state is a completed sign-in: reload where they are, now signed in. */
const signedIn = () => window.location.assign(window.location.pathname + window.location.search);

const notYet = "Your account needs an extra sign-in step these screens don’t offer yet. Contact AutoVouch support.";

export function SignInScreen() {
  const auth = useAuth();
  const native = auth.native!;
  const scopes = signInScopes(auth.auth.scope!);
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resend = useCountdown(30);

  const resetLink = `/reset-password?email=${encodeURIComponent(email.trim())}`;

  const start = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    const username = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(username)) return setError("Enter your work email.");
    setBusy(true);
    setError(null);
    try {
      const result = await native.signIn({ username, scopes });
      // Read before the state checks below, which narrow the result as they go.
      const error = result.error;
      const extraStep = result.isMfaRequired() || result.isAuthMethodRegistrationRequired();
      if (result.isPasswordRequired()) setStep({ kind: "password", state: result.state });
      else if (result.isCodeRequired()) { setCode(""); resend.restart(); setStep({ kind: "code", state: result.state }); }
      else if (result.isCompleted()) signedIn();
      else if (extraStep) setError(notYet);
      else if (error?.isUserNotFound()) setError(auth.auth.selfServeSignUp ? "There’s no account with that email. Check it, or create one." : "There’s no account with that email. Check it, or ask your administrator for an invite.");
      else if (error?.isInvalidUsername()) setError("That doesn’t look like an email address.");
      else if (isLocked(error)) setStep({ kind: "locked" });
      else setError(wrong("email step", error));
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (step.kind !== "password") return;
    if (!password) return setError("Enter your password.");
    setBusy(true);
    setError(null);
    try {
      const result = await step.state.submitPassword(password);
      const error = result.error;
      if (result.isMfaRequired() || result.isAuthMethodRegistrationRequired()) return setError(notYet);
      if (result.isCompleted()) return signedIn();
      if (isLocked(error)) return setStep({ kind: "locked" });
      if (isExpired(error)) { setStep({ kind: "email" }); return setError("That took a while, so we started over. Enter your email again."); }
      setError(error?.isInvalidPassword() ? "That password doesn’t match. Try again, or reset it." : wrong("password", error));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    if (step.kind !== "code") return;
    setBusy(true);
    setError(null);
    try {
      const result = await step.state.submitCode(value);
      const error = result.error;
      if (result.isMfaRequired() || result.isAuthMethodRegistrationRequired()) return setError(notYet);
      if (result.isCompleted()) return signedIn();
      setError(error?.isInvalidCode() ? "That code didn’t work. It may have expired: send a new one." : wrong("code", error));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (step.kind !== "code") return;
    setError(null);
    const result = await step.state.resendCode();
    if (result.isCodeRequired()) { setCode(""); resend.restart(); setStep({ kind: "code", state: result.state }); }
    else setError(wrong("resend code", result.error));
  };

  const back = () => { setStep({ kind: "email" }); setPassword(""); setError(null); };

  if (step.kind === "locked") {
    return (
      <AuthFrame>
        <AuthBadge tone="warn" icon="lock" />
        <AuthHeading title="Let’s take five" text="That’s a lot of tries in a row, so sign-in is paused for a few minutes. Can’t remember it? Reset it now." />
        <AuthActions column>
          <Button size="lg" fullWidth onClick={() => window.location.assign(resetLink)}>Reset password</Button>
          <p style={authNote}><TextButton onClick={back}>Back to sign in</TextButton></p>
        </AuthActions>
      </AuthFrame>
    );
  }

  if (step.kind === "password") {
    return (
      <AuthFrame>
        <h1 style={{ margin: 0, font: "800 38px/1.1 var(--font-display)", letterSpacing: "-0.02em", color: "var(--text-strong)" }}>Enter your password</h1>
        <EmailChip email={email.trim()} onChange={back} />
        <form noValidate onSubmit={submitPassword} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <PasswordInput label={<span style={{ display: "flex", justifyContent: "space-between", width: "100%" }}><span>Password</span><a href={resetLink} style={{ font: "700 14px var(--font-body)" }}>Forgot password?</a></span>} value={password} onChange={(e) => { setPassword(e.target.value); setError(null); }} autoComplete="current-password" autoFocus error={error ?? undefined} />
          </div>
          <Button type="submit" size="lg" fullWidth loading={busy}>Sign in</Button>
        </form>
      </AuthFrame>
    );
  }

  if (step.kind === "code") {
    const length = step.state.getCodeLength() || 8;
    return (
      <AuthFrame footer={<>Not you? <TextButton onClick={back} style={{ fontSize: 14 }}>Use another email</TextButton></>}>
        <AuthBadge tone="info" icon="mail" />
        <AuthHeading title="Check your inbox" text={<>We sent a {length}-digit code to <b style={{ color: "var(--text-strong)" }}>{email.trim()}</b>.</>} />
        <CodeInput length={length} value={code} invalid={!!error} onChange={(next) => { setCode(next); setError(null); if (next.length === length) void submitCode(next); }} />
        {error && <p role="alert" style={{ ...authText, fontSize: 14, color: "var(--status-mismatch-fg)", marginTop: -14 }}>{error}</p>}
        <AuthActions column>
          <Button size="lg" fullWidth loading={busy} onClick={() => void submitCode(code)} disabled={code.length < length}>Sign in</Button>
          <p style={authNote}>{resend.left > 0 ? `No code yet? Resend in 0:${String(resend.left).padStart(2, "0")}` : <>No code yet? <TextButton onClick={() => void resendCode()}>Send a new one</TextButton></>}</p>
        </AuthActions>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthHeading title="Welcome back" text="Sign in to keep your invoices moving." />
      <form noValidate onSubmit={start} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
        <Input size="lg" label="Work email" type="email" placeholder="you@company.com" autoComplete="email" autoFocus value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} error={error ?? undefined} />
        <AuthActions column>
          <Button type="submit" size="lg" fullWidth loading={busy} trailingIcon={<Icon name="arrow-right" size={18} />}>Continue</Button>
          {auth.auth.selfServeSignUp && <p style={authNote}>New to AutoVouch? <a href="/signup" style={{ fontWeight: 700 }}>Create an account</a></p>}
        </AuthActions>
      </form>
    </AuthFrame>
  );
}
